import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getCompanyShellCapabilities,
  type CompanyShellCapabilities,
} from "@/services/companyShell";

type UseCompanyShellCapabilitiesState = {
  loading: boolean;
  error: string | null;
  data: CompanyShellCapabilities | null;
  refresh: () => Promise<void>;
};

export function useCompanyShellCapabilities(
  companyId: string | undefined
): UseCompanyShellCapabilitiesState {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<CompanyShellCapabilities | null>(null);

  const load = useCallback(async () => {
    if (!companyId) {
      setLoading(false);
      setError("Missing company context.");
      setData(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const result = await getCompanyShellCapabilities(companyId);
      setData(result);

      if (!result) {
        setError("You do not have access to this company workspace.");
      }
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to load workspace capabilities.";
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  return useMemo(
    () => ({
      loading,
      error,
      data,
      refresh: load,
    }),
    [loading, error, data, load]
  );
}